import { computed, type ComputedRef } from 'vue'

import type { InsightActionGroup, InsightActionRow } from '../models/insightAction.types'
import type { MaybeUnknown, ResolutionReason } from '../models/insightActionReason'
import {
  SELECTION_CEILING,
  canFilterByChannel,
  columnTracks,
  filteredOutRows,
  isPickable,
  listedRows,
  pickableRows,
  selectAllKeys,
  selectedCoverage,
  visibleColumnKeys,
  visibleRows,
} from '../service/insightActionSelection'
import { useInsightActionStore } from '../stores/insightActionStore'

export interface ChannelChip {
  key: string
  total: number
  actionable: number
  reason: MaybeUnknown<ResolutionReason> | null
}

export interface ElsewhereRows {
  count: number
  target: InsightActionGroup | null
}

export interface UseInsightActionSelection {
  group: ComputedRef<InsightActionGroup | null>
  groups: ComputedRef<InsightActionGroup[]>
  listed: ComputedRef<InsightActionRow[]>
  pickable: ComputedRef<InsightActionRow[]>
  rows: ComputedRef<InsightActionRow[]>
  columnKeys: ComputedRef<string[]>
  gridTemplate: ComputedRef<string>
  channels: ComputedRef<ChannelChip[]>
  channelFilterEnabled: ComputedRef<boolean>
  elsewhere: ComputedRef<ElsewhereRows | null>
  allBlocked: ComputedRef<boolean>
  allSelected: ComputedRef<boolean>
  someSelected: ComputedRef<boolean>
  coverage: ComputedRef<number>
  isRowPickable: (row: InsightActionRow) => boolean
  toggleAll: () => void
}

export function useInsightActionSelection(): UseInsightActionSelection {
  const store = useInsightActionStore()

  const group = computed(() => store.activeGroup)
  const groups = computed(() => store.groups)
  const listed = computed(() => listedRows(group.value, groups.value.length))
  const pickable = computed(() => pickableRows(listed.value))
  const rows = computed(() =>
    visibleRows(listed.value, {
      channel: store.channelFilter,
      onlyApplicable: store.onlyApplicable,
    }),
  )

  const channels = computed<ChannelChip[]>(() =>
    (group.value?.summary.byChannel ?? []).map((channel) => ({
      key: channel.key,
      total: channel.total,
      actionable: channel.actionable,
      reason: channel.actionable > 0 ? null : channel.reason,
    })),
  )

  const elsewhere = computed<ElsewhereRows | null>(() => {
    const count = filteredOutRows(group.value).length
    if (!count || groups.value.length < 2) return null
    const siblings = groups.value.filter(
      (candidate) => candidate.key !== group.value?.key && candidate.tabKey === group.value?.tabKey,
    )
    return { count, target: siblings.length === 1 ? (siblings[0] ?? null) : null }
  })

  const allSelected = computed(
    () =>
      pickable.value.length > 0 && pickable.value.every((row) => store.selection.has(row.rowKey)),
  )

  function toggleAll(): void {
    if (allSelected.value) {
      store.clearSelection()
      return
    }
    store.setSelection(selectAllKeys(listed.value))
  }

  const columnKeys = computed(() => visibleColumnKeys(listed.value))

  return {
    group,
    groups,
    listed,
    pickable,
    rows,
    columnKeys,
    gridTemplate: computed(() => columnTracks(listed.value, columnKeys.value)),
    channels,
    channelFilterEnabled: computed(() => canFilterByChannel(listed.value)),
    elsewhere,
    allBlocked: computed(() => listed.value.length > 0 && pickable.value.length === 0),
    allSelected,
    someSelected: computed(() => store.selectedCount > 0),
    coverage: computed(() => selectedCoverage(listed.value, store.selection)),
    isRowPickable: isPickable,
    toggleAll,
  }
}

export { SELECTION_CEILING }
